import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { type DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useImagePrefetcher } from '@sorare/core/src/hooks/useImagePrefetcher.ts';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { ColorFormat } from '@sorare/core/src/lib/colors';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TinderCardSwipe } from '#src/components/commonCard/TinderCardSwipe/index.tsx';

import { PackTear } from './PackTear/index.tsx';
import type {
  RevealPackCardsDialog_anyCardInterface,
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables,
} from './__generated__/index.graphql.ts';

const StyledDialog = styled(Dialog)`
  @media ${laptopAndAbove} {
    max-height: 90vh;
  }
`;

const Root = styled(Vertical)`
  width: 100%;
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 100%;
  overflow: visible;
`;

const TinderCardSwipeWrapper = styled(Vertical)`
  width: 100%;
  flex: 1;
  &.hidden {
    opacity: 0;
    position: absolute;
    inset: 0;
  }
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const CLAIM_PACK_CARDS_ON_CLOSE_MUTATION = gql`
  mutation claimPackCardsOnCloseMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables
>;

type Props = {
  open: boolean;
  onClose?: () => void;
  cards: RevealPackCardsDialog_anyCardInterface[];
  onContinue: () => void;
  packId: string;
  packPictureUrl: string;
  isFirstPack?: boolean;
  initialPackAnimationDone?: boolean;
  primaryColor: ColorFormat;
  secondaryColor: ColorFormat;
  firstStepTitle?: ReactNode;
  showPlayerDetails?: boolean;
  cardSetSlug: string;
};

export const RevealPackCardsDialog = ({
  open,
  onClose,
  cards: propCards,
  onContinue,
  isFirstPack,
  packId,
  packPictureUrl,
  initialPackAnimationDone = false,
  firstStepTitle,
  showPlayerDetails,
  primaryColor,
  secondaryColor,
  cardSetSlug,
}: Props) => {
  const getCardSetConfig = useGetCardSetConfig();
  const setConfig = getCardSetConfig(cardSetSlug);
  const { background } = setConfig.visual.cardReveal;
  // Sort cards by drop rank so that the best cards are revealed last
  const cards = useMemo(
    () => sortBy(c => -1 * (c.dropRank ?? Infinity), [...propCards]),
    [propCards]
  );

  const [packAnimationDone, setPackAnimationDone] = useState(
    initialPackAnimationDone
  );
  const { sport } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  const [displayRecap, setDisplayRecap] = useState(false);

  const [lastCardRevealed, setLastCardRevealed] = useState(false);
  const [claimPackCardsOnClose] = useMutation(
    CLAIM_PACK_CARDS_ON_CLOSE_MUTATION
  );

  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const { showNotification } = useSnackNotificationContext();
  const packImageLoaded = useImagePrefetcher([
    proxyUrl(packPictureUrl, {
      cropWidth: CARD_SIZE,
    }),
  ]);

  const onNextCard = (index: number) => {
    const isLastCard = cards.length - 1 === index;
    if (isLastCard) {
      setLastCardRevealed(true);
    }
  };

  const handleClose = async () => {
    onClose?.();
    if (!lastCardRevealed || displayRecap) {
      if (packId) {
        await claimPackCardsOnClose({
          variables: {
            sport,
            input: {
              packId,
              chosenCardSlugs: cards
                .map(card => card.slug)
                .filter(slug =>
                  droppedCards.every(
                    droppedCard => droppedCard.cardSlug !== slug
                  )
                ),
            },
          },
        });
        if (!displayRecap) {
          showNotification('packOpeningInterrupted');
        }
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      hideHeader
      fullWidth
      maxWidth="xs"
      disableBackdropClick
      fullScreen={!isLaptopAndAbove}
    >
      {({ CloseButton }) => (
        <Root
          style={
            {
              '--primary-color': primaryColor,
              '--secondary-color': secondaryColor,
            } as CSSProperties
          }
        >
          <Background style={{ ...background }} />
          <TinderCardSwipeWrapper
            className={classNames({ hidden: !packAnimationDone })}
          >
            <TinderCardSwipe
              cards={cards}
              cardSetSlug={cardSetSlug}
              onContinue={onContinue}
              isFirstPack={isFirstPack}
              packId={packId}
              droppedCards={droppedCards}
              setDroppedCards={setDroppedCards}
              packAnimationDone={packAnimationDone}
              closeButton={
                !!handleClose &&
                !isFirstPack &&
                !!isLaptopAndAbove && <CloseButton onClose={handleClose} />
              }
              showPlayerDetails={showPlayerDetails}
              displayRecap={displayRecap}
              setDisplayRecap={setDisplayRecap}
              onNextCard={onNextCard}
            />
          </TinderCardSwipeWrapper>
          {!packAnimationDone && (
            <PackTear
              onDone={() => {
                setPackAnimationDone(true);
              }}
              firstCardUrl={cards.at(0)?.pictureUrl || ''}
              loading={!cards.length || !packImageLoaded}
              packPictureUrl={packPictureUrl}
              closeButton={
                !!handleClose &&
                !isFirstPack &&
                !!isLaptopAndAbove && <CloseButton onClose={handleClose} />
              }
              title={
                firstStepTitle || (
                  <Vertical as="span" center gap={1} className="text-center">
                    <svg
                      width="47"
                      height="43"
                      viewBox="0 0 47 43"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M30.8125 34.8584V43.001L24.1972 40.6484L17.581 43.001V34.8584L24.1972 31.377L30.8125 34.8584ZM15.3476 27.0508L15.6591 34.5576L8.65329 38.6279L7.36911 31.6865L2.03806 27.0957L9.04391 23.0244L15.3476 27.0508ZM45.6552 25.873L40.3252 30.4639L39.041 37.4053L32.0341 33.333L32.3457 25.8281L38.6494 21.8027L45.6552 25.873ZM23.373 14.6074C27.1872 14.6076 30.2793 17.7191 30.2793 21.5576C30.2789 25.3958 27.187 28.5076 23.373 28.5078C19.559 28.5077 16.4671 25.3959 16.4668 21.5576C16.4668 17.719 19.5588 14.6075 23.373 14.6074ZM23.373 16.6758C20.6944 16.6759 18.5234 18.8609 18.5234 21.5566C18.5237 24.2521 20.6946 26.4364 23.373 26.4365C26.0506 26.4364 28.2223 24.2521 28.2226 21.5566C28.2226 18.8609 26.0516 16.6759 23.373 16.6758ZM22.7978 18.5195C22.8201 18.5275 22.8401 18.5412 22.8545 18.5596L22.8554 18.5586C23.2278 19.0256 23.7066 19.3946 24.2519 19.6338C24.7973 19.8729 25.3931 19.9748 25.9863 19.9307C26.0101 19.9283 26.0342 19.932 26.0556 19.9424C26.0771 19.9528 26.0956 19.9704 26.1084 19.9912C26.1209 20.0119 26.1267 20.0358 26.1259 20.0596C26.1259 20.0835 26.1181 20.1071 26.1054 20.1279C25.7794 20.6287 25.579 21.2025 25.5224 21.7979C25.4652 22.3939 25.5535 22.9945 25.7783 23.5488C25.7868 23.571 25.789 23.5942 25.7851 23.6172C25.7811 23.6404 25.7709 23.6631 25.7558 23.6807C25.7407 23.6981 25.7194 23.7106 25.6972 23.7178C25.675 23.7249 25.6509 23.7251 25.6279 23.7188C25.0538 23.5607 24.4493 23.5466 23.8672 23.6758C23.286 23.805 22.7445 24.0754 22.29 24.4629C22.2717 24.478 22.2494 24.488 22.2256 24.4912C22.2026 24.4943 22.1785 24.491 22.1572 24.4814C22.1358 24.4718 22.117 24.4566 22.1035 24.4375C22.09 24.4175 22.0827 24.3943 22.081 24.3711C22.0523 23.7733 21.8785 23.191 21.5771 22.6758C21.2757 22.1599 20.8541 21.7264 20.3486 21.4111C20.3288 21.3984 20.3131 21.3808 20.3027 21.3594C20.2924 21.3378 20.2885 21.314 20.29 21.29C20.2924 21.267 20.3011 21.2439 20.3144 21.2256C20.3287 21.2064 20.3478 21.1915 20.3691 21.1826C20.9256 20.9708 21.4226 20.6254 21.8174 20.1777C22.212 19.7292 22.4918 19.1911 22.6338 18.6094C22.6393 18.5863 22.6523 18.5658 22.6689 18.5498C22.6856 18.5331 22.7065 18.5213 22.7295 18.5156C22.7524 18.5109 22.7765 18.5116 22.7978 18.5195ZM14.958 9.66602L14.6445 17.1738L8.34079 21.1992L1.33493 17.1289L6.66599 12.5371L7.95212 5.5957L14.958 9.66602ZM39.623 11.3145L44.956 15.9043L37.9492 19.9766L31.6445 15.9512L31.333 8.44336L38.3388 4.37305L39.623 11.3145ZM29.4101 8.14258L22.7949 11.624L16.1787 8.14258V0L22.7949 2.35254L29.4101 0V8.14258Z"
                        fill="white"
                      />
                    </svg>
                    <FormattedMessage
                      defaultMessage="Trace the line{br}to open your pack"
                      values={{
                        br: <br />,
                      }}
                    />
                  </Vertical>
                )
              }
            />
          )}
        </Root>
      )}
    </StyledDialog>
  );
};

RevealPackCardsDialog.fragments = {
  anyCardInterface: gql`
    fragment RevealPackCardsDialog_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      dropRank
      ...TinderCardSwipe_anyCardInterface
    }
    ${TinderCardSwipe.fragments.anyCardInterface}
  ` as TypedDocumentNode<RevealPackCardsDialog_anyCardInterface>,
};
